"use strict";
/**
 * Shared key-transformation utilities used by both client and server for
 * datastore-safe key encoding and URL↔key conversion.
 *
 * The datastore forbids the characters `.`, `#`, `$`, `/`, `[`, `]`, and `%` in
 * keys — a constraint that originated with Firebase Realtime Database and is
 * honored by all adapters. The encoding scheme uses a custom percent-like
 * escape (e.g. `.` → `%d`) rather than standard URI encoding because the two
 * restriction sets differ and are not interchangeable. The reversible pair
 * handles general strings; the lossy slug variant strips diacritics and
 * non-alphanumerics for human-readable instance keys and cannot be reversed —
 * use it only where readability matters more than round-trip fidelity.
 *
 * This file must not import Node-only or browser-only modules — it is consumed
 * by both the React/Expo client and the Node.js server.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.stringToFbKey = stringToFbKey;
exports.fbKeyToString = fbKeyToString;
exports.keyToUrl = keyToUrl;
exports.urlToKey = urlToKey;
exports.instanceNameToLossyKey = instanceNameToLossyKey;
exports.deepClone = deepClone;
/**
 * Convert a string to a datastore-safe key by escaping special characters.
 * Datastore keys cannot contain: . # $ / [ ] % (Function name preserved from
 * when the datastore was Firebase Realtime Database.)
 */
function stringToFbKey(input) {
    const mapping = {
        '.': '%d',
        '#': '%h',
        $: '%s',
        '/': '%f',
        '[': '%l',
        ']': '%r',
        '%': '%%',
        "'": '%q',
    };
    return input.replace(/[.#$\/\[\]%']/g, (match) => mapping[match]);
}
/**
 * Convert a Firebase-safe key back to the original string.
 */
function fbKeyToString(input) {
    const reverseMapping = {
        '%d': '.',
        '%h': '#',
        '%s': '$',
        '%f': '/',
        '%l': '[',
        '%r': ']',
        '%%': '%',
        '%q': "'",
    };
    return input.replace(/%d|%h|%s|%f|%l|%r|%%|%q/g, (match) => reverseMapping[match]);
}
/**
 * Convert a URL-encoded key to a plain URL string.
 */
function keyToUrl(key) {
    return fbKeyToString(decodeURIComponent(key));
}
/**
 * Convert a URL to a URL-encoded Firebase-safe key.
 */
function urlToKey(url) {
    return encodeURIComponent(stringToFbKey(url));
}
/**
 * Convert an instance name to a lossy key.
 *
 * NOTE: lossy because it removes all non-alphanumeric characters and replaces
 * spaces with dashes. For a reversible transformation, use urlToKey() instead.
 */
function instanceNameToLossyKey(string) {
    return string
        .toLowerCase()
        .trim()
        .replace(/\u00df/g, 'ss')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ /g, '-')
        .replace(/[\u2018\u2019\u0027\u02bc\u0060\u201c\u201d\u201e\u201a\u00ab\u00bb\u2039\u203a\u2032\u2033]/g, '-')
        .replace(/\?/g, '')
        .replace(/[^a-zA-Z0-9-]/g, '')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '');
}
function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}
//# sourceMappingURL=keyutil.js.map